import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Box, Button, Drawer, Theme } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import styled from '@emotion/styled'

import { COLORS } from '@constants/theme'

import NavSection from './NavSection'

const LogoWrapper = styled(Box)`
  img {
    width: 100%;
    transition: width 0.3s ease-in-out;
  }
`

export interface SidebarItem {
  title: string
  path: string
  icon: React.ReactElement
  childItems?: SidebarItem[]
}

export interface SidebarSection {
  title: string
  items: SidebarItem[]
}

interface SidebarProps {
  onMobileClose: (isOpen?: boolean) => void
  openMobile: boolean
  logoLinkUrl?: string
  sections: SidebarSection[]
}

export default function Sidebar({ onMobileClose, openMobile, sections, logoLinkUrl = '/' }: SidebarProps) {
  const [isOpen, setIsOpen] = useState(openMobile)
  const location = useLocation()
  const mediumAndUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  const navWidth = (theme: Theme) => (isOpen ? theme.sidebar.width.md : theme.sidebar.collapsedWidth.md)

  const torqLogoWrapperStyling: React.CSSProperties = {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'end',
    width: '100%',
    marginLeft: 1,
    margin: '.75rem 0',
  }

  const ibLogoWrapperStyling: React.CSSProperties = {
    display: 'flex',
    alignItems: 'end',
    justifyContent: 'center',
    width: '100%',
    flexGrow: '100%',
    marginBottom: '1rem',
  }

  // Sync desktop open state from redux; on mobile manage locally (start closed)
  useEffect(() => {
    if (mediumAndUp) setIsOpen(openMobile)
  }, [openMobile, mediumAndUp])

  // When switching to mobile, close the sidebar
  useEffect(() => {
    if (!mediumAndUp) setIsOpen(false)
  }, [mediumAndUp])

  const handleToggle = () => {
    const updatedOpenedState = !isOpen
    setIsOpen(updatedOpenedState)
    onMobileClose(updatedOpenedState)
  }

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch',
        marginBottom: `${!mediumAndUp ? '1.5rem' : 0}`,
      }}
    >
      <Box display="flex" justifyContent="space-between" flexDirection="column" height="100%">
        <Box display="flex" flexDirection="column">
          {!mediumAndUp ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 2, paddingX: 1 }}>
              <LogoWrapper sx={torqLogoWrapperStyling}>
                <a href={logoLinkUrl}>
                  <img src={'/img/workforce-logo-lg-white.svg'} alt="TorqCloud" style={{ width: '100%' }} />
                </a>
              </LogoWrapper>
              <ChevronLeft
                onClick={() => onMobileClose()}
                sx={{
                  cursor: 'pointer',
                  background: COLORS.GRAY_BLUE,
                  marginTop: 2,
                }}
              />
            </Box>
          ) : (
            <LogoWrapper sx={torqLogoWrapperStyling}>
              <a href={logoLinkUrl}>
                <img src={'/img/workforce-logo-lg-white.svg'} alt="TorqCloud" style={{ width: '100%' }} />
              </a>
            </LogoWrapper>
          )}
          <Box sx={{ flexGrow: 1 }}>
            {sections.map(section => (
              <NavSection key={section.title} pathname={location.pathname} collapsed={!isOpen} {...section} />
            ))}
          </Box>
        </Box>
        <LogoWrapper sx={ibLogoWrapperStyling}>
          <img src={'/img/company-logo.svg'} style={{ width: '100%', maxWidth: '137px' }} alt="Agile Defense" />
        </LogoWrapper>
      </Box>
    </Box>
  )

  const collapsedContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Box display="flex" justifyContent="space-between" flexDirection="column" height="100%">
        <Box display="flex" flexDirection="column">
          <LogoWrapper sx={torqLogoWrapperStyling}>
            <a href={logoLinkUrl} style={{ textAlign: 'center' }}>
              <img src={'/img/workforce-logo-sm-white.svg'} alt="TorqCloud" />
            </a>
          </LogoWrapper>
          <Box sx={{ flexGrow: 1 }}>
            {sections.map(section => (
              <NavSection key={section.title} pathname={location.pathname} collapsed={true} {...section} />
            ))}
          </Box>
        </Box>
        <LogoWrapper sx={ibLogoWrapperStyling}>
          <img
            src={'/img/company-logo-sm.svg'}
            alt="Agile Defense"
            style={{
              maxWidth: '34px',
              transition: 'width 0.5s ease-in-out',
            }}
          />
        </LogoWrapper>
      </Box>
    </Box>
  )

  return (
    <>
      {mediumAndUp ? (
        <Box
          id="app__sidebar-inner"
          sx={{
            position: 'relative',
            height: '100%',
            width: navWidth,
            flexShrink: 0,
            transition: (theme: Theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.standard,
              }),
          }}
        >
          <Box
            id="app__sidebar-panel"
            sx={{
              background: COLORS.GRAY_BLUE,
              height: 'calc(100% - 2rem)',
              width: '100%',
              my: '1rem',
              padding: '.75rem',
              borderRadius: '16px',
              overflow: 'hidden',
              transition: (theme: Theme) =>
                theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.standard,
                }),
            }}
          >
            {isOpen ? content : collapsedContent}
          </Box>
          <Button
            onClick={handleToggle}
            sx={{
              position: 'absolute',
              width: '25px',
              paddingRight: '0px',
              background: COLORS.GRAY_BLUE,
              right: '-16px',
              top: '4.5rem',
              borderRadius: '8px',
              cursor: 'pointer',
              zIndex: 1,
              minWidth: 'unset',
              '&:hover': {
                background: COLORS.GRAY_BLUE,
              },
            }}
          >
            {isOpen ? (
              <ChevronLeft sx={{ fontSize: '1rem', transform: 'translateX(-2px)' }} color={'action'} />
            ) : (
              <ChevronRight sx={{ fontSize: '1rem', transform: 'translateX(-2px)' }} color={'action'} />
            )}
          </Button>
        </Box>
      ) : (
        <>
          <Drawer
            anchor="left"
            variant="temporary"
            open={isOpen}
            onClose={() => {
              setIsOpen(false)
              onMobileClose(false)
            }}
            PaperProps={{
              sx: {
                background: COLORS.GRAY_BLUE,
                height: 'calc(95%) !important',
                width: 70,
                margin: '1rem',
                padding: '.75rem',
                borderRadius: '16px',
                borderStyle: 'none!important',
                position: 'absolute',
                top: '0',
                left: '0',
                zIndex: 1,
              },
            }}
          >
            {collapsedContent}
          </Drawer>
          <Box
            onClick={() => handleToggle()}
            sx={{
              top: '5rem',
              position: 'fixed',
              transform: isOpen ? 'translateX(80px)' : 'translateX(0)',
              transition: 'transform 0.23s ease-in-out',
              width: '22px',
              height: '28px',
              backgroundColor: COLORS.GRAY_BLUE,
              borderTopRightRadius: '8px',
              borderBottomRightRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 1300,
            }}
          >
            {isOpen ? (
              <ChevronLeft sx={{ fontSize: '1rem' }} color={'action'} />
            ) : (
              <ChevronRight sx={{ fontSize: '1rem' }} color={'action'} />
            )}
          </Box>
        </>
      )}
    </>
  )
}
